import {
    SxProps,
    Box,
    Stack,
    TextField,
    InputAdornment,
    Link,
    Autocomplete,
    Paper,
    ClickAwayListener,
    IconButton,
    AutocompleteRenderInputParams,
    Typography,
} from '@mui/material';
import Icon from '@mdi/react';
import React, { useState, useEffect, Dispatch } from 'react';
import { mdiMagnify, mdiArrowLeft, mdiChevronDown, mdiClose } from '@mdi/js';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IAppReduxStore } from '../../../redux/store';
import {
    vars,
    headingFontBold,
    labelFontSemiBold,
    projectSelectionListHeight,
    projectSelectionLinkHeight,
    projectSelectionLinkPadding,
    projectSelectionSearchFieldHeight,
} from '../../../Theme';
import DatabaseLogo from './DatabaseLogo';
import { getProjects } from '../../../redux/user/sliceUserProjects';
import AppTooltip from '../AppTooltip/AppTooltip';

/** Autocomplete styles. */
const autocompleteSx: SxProps = {
    ml: 3,
    mr: 3,
    height: 40,
    borderRadius: 1,
    '&:hover': {
        backgroundColor: vars.brandDarkBlueSecondary,
    },
    '& .MuiInputBase-input': {
        ...headingFontBold,
        color: vars.white,
        fontWeight: 700,
        fontSize: '14px',
    },
    '& .MuiAutocomplete-root, .MuiOutlinedInput-root': { padding: 0 },
    '& .MuiButtonBase-root': {
        paddingRight: 0,
        paddingLeft: 0,
    },
};

/** Paper styles. */
const paperSx: SxProps = {
    '& .MuiAutocomplete-option': {
        ...labelFontSemiBold,
        fontWeight: 500,
        fontSize: '16px',
        color: vars.font,
        height: projectSelectionListHeight,
    },
    '& .MuiAutocomplete-option[aria-selected="true"]': {
        color: vars.brandGreen,
    },
};

/** Stack styles that wraps backToProject link. */
const linkWrapperSx: SxProps = {
    height: projectSelectionLinkHeight,
    padding: projectSelectionLinkPadding / 8,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
};

/** Typography styles. */
const typographySx: SxProps = {
    margin: 0,
    fontWeight: 400,
    fontSize: '12px',
    color: vars.white,
    height: 15,
    position: 'absolute',
    bottom: 2,
    left: 55,
};

/** Ellipsis text styles. */
const ellipsisSx: SxProps = {
    width: '210px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
};

/** TextField styles. */
const textFieldSx: SxProps = {
    input: { position: 'relative', top: -8 },
    '& .MuiInputBase-input': {
        fontSize: '14px',
    },
    '& .MuiInputBase-root': {
        '& fieldset': {
            border: 'none',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
        },
    },
};

const searchInputSx: SxProps = {
    input: { py: 1 },
    fontSize: '14px',
    fontWeight: 400,
    '& .MuiOutlinedInput-root.Mui-focused': {
        '& > fieldset': {
            borderColor: vars.font,
        },
    },
};

/**
 * Component that shows information about the current project.
 */
export default ({ style, open, toggleDropdownOnclick, closeDropdown }: IHeaderProjectInfoProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'header' });

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get relevant values from Redux store. */
    const metadata = useSelector((store: IAppReduxStore) => store.projectMetadata.data);
    const projects = useSelector((store: IAppReduxStore) => store.userProjects.data);

    const [selectValue, setSelectValue] = useState<string>('');
    const [searchValue, setSearchValue] = useState<string>('');
    const [databaseType, setDatabaseType] = useState<string>('');

    useEffect(() => {
        !projects.length && dispatch(getProjects());

        metadata !== null && selectValue === '' && setSelectValue(metadata[replicaId]?.Title);

        const foundProject = projects.find(project => project.replicaId === replicaId);

        setDatabaseType(foundProject?.type || '');
    }, [projects, metadata, selectValue, databaseType]);

    /**
     * Function to set the selected value on dropdown,
     * open the selected database in the new tab and set the previous database in the current tab.
     */
    const handleSelectChange = (_: React.SyntheticEvent<Element, Event>, value: string) => {
        window.open(`/project/${getReplicaId(value)}`, '_blank');
    };

    /**
     * Function that returns the replicaId.
     */
    const getReplicaId = (value: string) => {
        return projects.filter(project => project.title === value)[0]?.replicaId;
    };

    /**
     * Function that returns options value array.
     */
    const getOptionsValue = () => {
        return projects.map(proj => proj.title);
    };

    const searchField = (options: ICustomPaperComponentProps) => {
        return (
            <Stack
                sx={{ padding: 1, height: projectSelectionSearchFieldHeight }}
            >
                <TextField
                    {...options}
                    fullWidth
                    value={searchValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setSearchValue(e.target.value)
                    }
                    inputRef={input => searchValue && input && input.focus()}
                    placeholder={t('search')}
                    sx={searchInputSx}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position='end'>
                                    {searchValue.length > 0 && (
                                        <IconButton
                                            onClick={e => {
                                                e.preventDefault();
                                                setSearchValue('');
                                            }}
                                        >
                                            <Icon path={mdiClose} size={1} />
                                        </IconButton>
                                    )}
                                    <Icon path={mdiMagnify} size={1} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Stack>
        );
    };

    const backToProjects = (options: ICustomPaperComponentProps) => {
        return (
            <Stack {...options} sx={linkWrapperSx}>
                <Box sx={{ cursor: 'pointer', marginRight: 1 }}>
                    <Icon path={mdiArrowLeft} size={1} color={vars.brandGreen} />
                </Box>

                <Link href='/' underline='none' sx={{ ...headingFontBold }}>
                    {t('projectSelection')}
                </Link>
            </Stack>
        );
    };

    const renderInput = (params: AutocompleteRenderInputParams) => {
        return (
            <Stack sx={{ position: 'relative' }}>
                <AppTooltip title={params.inputProps.value}>
                    <TextField
                        {...params}
                        sx={textFieldSx}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                inputProps: { ...params.inputProps, readOnly: true },
                                onClick: toggleDropdownOnclick,
                                startAdornment: (
                                    <Box sx={{ maxHeight: 40, maxWidth: 40, mr: 1 }}>
                                        <DatabaseLogo size={40} />
                                    </Box>
                                ),
                            },
                        }}
                    />
                </AppTooltip>
                <Typography
                    sx={{
                        ...typographySx,
                        ...ellipsisSx,
                    }}
                >
                    {databaseType}
                </Typography>
            </Stack>
        );
    };

    const CustomPaper = (options: ICustomPaperComponentProps) => {
        return (
            <ClickAwayListener onClickAway={closeDropdown}>
                <Paper
                    sx={{
                        ...paperSx,
                        '& .MuiAutocomplete-listbox': {
                            minHeight: projects.length < 10 && 'unset',
                            maxHeight:
                                projects.length > 10 ? projectSelectionListHeight * 10 : 'unset',
                            overflow: 'auto',
                        },
                    }}
                >
                    {searchField(options)}
                    {options.children}
                    {backToProjects(options)}
                </Paper>
            </ClickAwayListener>
        );
    };

    const filterOptions = (options: string[]) => {
        return searchValue.length
            ? options.filter(option => option.toLowerCase().includes(searchValue.toLowerCase()))
            : options;
    };

    return (
        <Autocomplete
            open={open}
            sx={{
                ...autocompleteSx,
                ...style,
                backgroundColor: open ? vars.brandDarkBlueSecondary : vars.brandDarkBlue,
            }}
            onChange={handleSelectChange}
            options={getOptionsValue()}
            value={selectValue}
            renderInput={renderInput}
            popupIcon={<Icon path={mdiChevronDown} size={1} color={vars.white} />}
            PaperComponent={CustomPaper}
            filterOptions={filterOptions}
            noOptionsText={t('noOptions')}
            disableClearable
            loading={searchValue === ''}
        />
    );
};

/**
 * Interface that describes the props for HeaderProjectInfo component.
 */
export interface IHeaderProjectInfoProps {
    /** styles. */
    style?: SxProps;

    /** State that shows if the dropdown is open or not. */
    open: boolean;

    /** Onclick event to handle the dropdown. */
    toggleDropdownOnclick: (e: React.MouseEvent<HTMLElement>) => void;

    /** Onclick event to handle the clickaway listener. */
    closeDropdown: (e: MouseEvent | TouchEvent) => void;
}

/**
 * Interface that describes the props for custom Paper Component.
 */
export interface ICustomPaperComponentProps {
    /** children of the Paper Component. */
    children: React.ReactNode;

    /** className of the Paper Component. */
    className?: string;
}
